import React, { useState, useCallback, useRef, useEffect, MouseEventHandler } from 'react'
import { Field, useFieldState } from 'formular'
import { useUniqueId } from 'hooks'
import cx from 'classnames'
import { debounce as handleDebounce } from 'helpers'
import { required } from 'helpers/validators'
import { getGlobalHtmlAttrs } from 'helpers/getters'
import type { GlobalHtmlAttributes } from 'helpers/getters'

import { Href } from 'components/navigation'
import { Tooltip, TooltipPosition } from 'components/feedback'
import { Icon, IconName } from 'components/dataDisplay'

import DatePicker, { DatePickerProps } from '../DatePicker/DatePicker'
import TimePicker from '../TimePicker/TimePicker'

import masks from './util/masks'

import s from './Input.scss'


export const styles = [ 'light', 'dark' ] as const

export type InputStyle = typeof styles[number]

export type InputProps = Omit<GlobalHtmlAttributes<'HTMLInputElement'>, 'data-testid'> & {
  className?: string
  id?: string
  field: Field<string | number>
  mask?: 'time' | 'date' | 'ipRange' | 'year' | 'year_1800_1990' | 'year_1900_now' | 'capitalize'
  | 'phone' | 'price' | 'number' | 'period'
  pattern?: string
  style?: InputStyle
  label: string
  note?: string
  title?: string
  withCalendar?: boolean
  calendarWithPeriod?: boolean
  withTimePicker?: boolean
  isPickOnlyBeforeToday?: boolean
  onFocus?: React.FocusEventHandler<HTMLInputElement>
  onBlur?: React.FocusEventHandler<HTMLInputElement>
  onChange?: (value: any) => void
  onSuggestionClick?: (value: any) => void
  dataTestId?: string
  icon?: IconName
  iconPosition?: 'left' | 'right'
  pickerPosition?: 'top' | 'bottom'
  iconTooltip?: string
  tooltipPosition?: TooltipPosition
  format?: DatePickerProps['format']
  debounce?: number
  onIconClick?: () => void
  loading?: boolean
  suggestions?: string[]
}

const Input: React.FC<InputProps> = (props) => {
  const {
    className, id, field, style = 'light', title,
    label, note, mask, pattern, disabled, icon, iconPosition = 'left', iconTooltip, tooltipPosition = 'top-right',
    onIconClick,
    withCalendar = false, calendarWithPeriod = false, pickerPosition = 'bottom',
    onFocus, onBlur, onChange, onSuggestionClick, withTimePicker = false,
    dataTestId = 'input', isPickOnlyBeforeToday, format, loading, debounce, suggestions,
    ...otherProps
  } = props

  const inputRef = useRef<HTMLInputElement>(null)
  const containerRef = useRef<HTMLDivElement>(null)

  const [ isFocused, setFocused ] = useState(Boolean(otherProps.autoFocus))
  const [ isBlurBlocked, setBlurBlocked ] = useState(false)
  const [ isSuggestionsShown, setSuggestionsShown ] = useState(false)

  const { value, error } = useFieldState<string | number>(field)

  const isBlurBlockedRef = useRef(isBlurBlocked)
  isBlurBlockedRef.current = isBlurBlocked

  useEffect(() => {
    if (!otherProps.autoFocus) {
      return () => {
        setFocused(true)
      }
    }
  }, [ otherProps.autoFocus ])

  useEffect(() => {
    const checkOutsideClick = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setFocused(false)
        setBlurBlocked(false)
        setSuggestionsShown(false)
      }
    }

    document.addEventListener('click', checkOutsideClick, true)

    return () => {
      document.removeEventListener('click', checkOutsideClick, true)
    }
  }, [ isBlurBlocked ])

  useEffect(() => {
    if ((withCalendar || withTimePicker) && isFocused && !isBlurBlocked) {
      setBlurBlocked(true)
    }
  }, [ withCalendar, isFocused, isBlurBlocked, withTimePicker ])

  useEffect(() => {
    if (isBlurBlockedRef.current) {
      setBlurBlocked(false)
      setFocused(false)
    }
  }, [ value ])

  const handleFocus = useCallback((event) => {
    setFocused(true)
    if (suggestions?.length) {
      setSuggestionsShown(true)
    }

    if (typeof onFocus === 'function') {
      onFocus(event)
    }
  }, [ onFocus, suggestions?.length ])

  const handleBlur = useCallback(async (event) => {
    if (isBlurBlocked) return
    await (field.validate() as unknown as Promise<any>)
    setFocused(false)

    if (typeof onBlur === 'function') {
      onBlur(event)
    }
  }, [ isBlurBlocked, field, onBlur ])

  useEffect(() => {
    if (error === 'Введите значение' && value) {
      field.validate()
    }
  }, [ value, field, error ])

  const handleChange = useCallback((event) => {
    let value = event.target.value

    const applyMask = masks[mask]

    if (typeof applyMask === 'function') {
      value = applyMask(value)
    }
    else if (pattern) {
      value = value.replace(new RegExp(`[^${pattern}]+`, 'g'), '')
    }

    field.set(value)

    if (field.state.value === value) {
      // Imagine we have input with mask: "XX/XX/XXX" for date. user can type incorrect
      // value in field: "11/A", in this case "A" will be removed from value and field.set
      // be called with value "11", previous value (before user type "A") was "11" - react
      // will not re-render the component, but native "uncontrolled" change in the input already
      // happened and user sees "11/A" in the input. To avoid this we need replace input's value manually.
      inputRef.current.value = value
    }

    if (typeof onChange === 'function') {
      onChange(value)
    }
  }, [ mask, pattern, field, onChange ])

  useEffect(() => {
    if (isFocused) {
      inputRef.current.focus()
    }
  }, [ isFocused ])

  const isFilled = value !== ''
  const isErrored = Boolean(error)
  const isRequired = field.validators.includes(required)

  const inputAreaClassName = cx(s.inputArea,
    'flex radius-8',
    s[`style-${style}`], {
      [s.focused]: isFocused,
      [s.filled]: isFilled,
      [s.errored]: isErrored,
      [s.disabled]: disabled || loading,
    })

  const textContentClassName = cx(s.textContent,
    'inline-flex flex-col justify-center relative', {
      ['ml-8px']: Boolean(icon && iconPosition === 'left'),
      ['mr-8px']: Boolean(icon && iconPosition === 'right'),
    })

  const uniqueId = useUniqueId('input-')
  const controlId = id || uniqueId
  const htmlAttrs = getGlobalHtmlAttrs(otherProps)

  const handleIconClick = useCallback<MouseEventHandler<HTMLAnchorElement>>((event) => {
    onIconClick()
    event.preventDefault()
    event.stopPropagation()
  }, [ onIconClick ])

  const handleSuggestionClick: MouseEventHandler<HTMLLIElement> = useCallback((event) => {
    const { target } = event
    // @ts-expect-error wrong typing
    const suggestion = target.innerText
    setFocused(false)

    setSuggestionsShown(false)
    // @ts-expect-error wrong typing
    const index = Array.from(target.parentElement.childNodes).findIndex((node) => node === target)
    field.set(suggestion)
    if (typeof onSuggestionClick === 'function') {
      onSuggestionClick(index)
    }
    if (typeof onChange === 'function') {
      onChange(suggestion)
    }
  }, [ field, onChange, onSuggestionClick ])

  const iconNode = (
    <Href
      className={cx(s.icon, 'flex my-12px')}
      onClick={typeof onIconClick === 'function' ? handleIconClick : null}
    >
      {
        iconTooltip ? (
          <Tooltip message={iconTooltip} position={tooltipPosition}>
            <Icon
              name={icon}
            />
          </Tooltip>
        ) : (
          <Icon
            name={icon}
          />
        )
      }
    </Href>
  )

  return (
    <div
      ref={containerRef}
      className={cx(className, 'relative', 'radius-8')}
    >
      <div
        className={inputAreaClassName}
        onClick={(disabled || loading) ? null : handleFocus}
      >
        {
          Boolean(icon && iconPosition === 'left') && (
            iconNode
          )
        }
        <div className={textContentClassName}>
          <label className={cx(s.label, 'absolute')} htmlFor={controlId}>
            {loading ? 'Загрузка...' : label}
          </label>
          <input
            {...htmlAttrs}
            id={controlId}
            className={cx(s.input, 'mt-16px')}
            ref={inputRef}
            //@ts-ignore
            value={loading ? '' : value}
            title={title}
            onFocus={handleFocus}
            onBlur={handleBlur}
            readOnly={withCalendar}
            onChange={debounce ? handleDebounce(handleChange, debounce) : handleChange}
            data-testid={dataTestId}
            aria-invalid={isErrored}
            aria-required={isRequired}
            disabled={disabled || loading}
          />
        </div>
        {
          Boolean(icon && iconPosition === 'right') && (
            iconNode
          )
        }
      </div>
      {isSuggestionsShown && (
        <ul
          style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            backgroundColor: 'white',
            // border: '1px solid #ccc',
            borderRadius: '8px',
            listStyle: 'none',
            padding: 0,
            margin: 0,
            zIndex: 1000,
            maxHeight: '200px',
            overflowY: 'auto',
            boxShadow: '0 9px 28px rgba(17, 48, 121, 0.18)',
          }}
        >
          {suggestions.map((suggestion, index) => (
            <li
              key={index}
              onClick={handleSuggestionClick}
              style={{
                padding: '12px 16px',
                cursor: 'pointer',
                height: '44px',
              }}
            >
              {suggestion}
            </li>
          ))}
        </ul>
      )}
      {
        Boolean(note && !isErrored) && (
          <div
            className={cx(s.note, {
              'pl-24px': Boolean(icon && iconPosition === 'left'),
            })}
          >
            {note}
          </div>
        )
      }
      {
        isErrored && (
          <div className={cx(s.error, 'absolute', {
            [ 'pl-24px' ]: Boolean(icon && iconPosition === 'left'),
          }
          )} data-testid={`${dataTestId}Error`}>
            {error}
          </div>
        )
      }
      {
        withCalendar && isFocused && (
          <DatePicker
            className={cx(s.picker, 'mt-8px shadow-titanic-1', {
              [s.top]: pickerPosition === 'top',
            })}
            field={field as Field<string>}
            format={format || 'DD.MM.YYYY'}
            pickPeriod={calendarWithPeriod}
            isPickOnlyBeforeToday={isPickOnlyBeforeToday}
          />
        )
      }
      {
        withTimePicker && isFocused && (
          <TimePicker
            className={cx(s.picker, 'mt-8px shadow-titanic-1', {
              [s.top]: pickerPosition === 'top',
            })}
            field={field as Field<string>}
            withShadow
          />
        )
      }
    </div>
  )
}


export default Input
